<template>
	<fragment v-if="isUserAllowedInLevel5or6">
		<BaseHeader :title="$t('customer_base').toString()">
			<div class="flex items-center" v-if="isUserAllowedInLevel5or6">
				<BaseButton @click="$router.push({ name: 'management.customer.create' })">
					<font-awesome-icon :icon="['fa', 'plus']" class="w-5 h-5 pr-1" size="lg"></font-awesome-icon>{{ $t('store_customer') }}</BaseButton
				>
			</div>
		</BaseHeader>

		<OverviewSearch
			ref="overviewSearch"
			@search="search"
			@directSearch="directSearch"
			:is-search-running="isSearchRunning"
			:show-col="showCol"
			class="mb-8"
			:search-value-placeholder="$t('customer_search_placeholder')"
		></OverviewSearch>

		<div v-if="customers">
			<div class="flex justify-between align-middle mb-4 mx-8">
				<PaginationNumberOfPages :current-page="current_page" :last-page="last_page" :total="total" class="mr-4 self-center" />
				<div class="flex">
					<PaginationSizeSelect v-model="per_page" style="min-width: 140px; max-width: 140px" v-show="customers && customers.length" />
					<BasePagination v-model="current_page" :last-page="last_page" :total="total" :is-blocked="blockSearch" />
				</div>
			</div>
			<div class="flex flex-col">
				<div>
					<div class="align-middle inline-block min-w-full overflow-hidden border-b border-t border-gray-200">
						<div class="m-4" v-if="this.customers && this.customers.length <= 0">
							<b>{{ $t('no_entries_found') }}</b>
						</div>
						<table class="min-w-full" v-if="this.customers && this.customers.length > 0">
							<thead>
								<tr>
									<OverviewTableHeader
										:current-sort="currentSortField === 'licensePlate'"
										:current-sort-dir="currentSortDirection"
										:label="$t('license_plate').toString()"
										@click.native="sortby('licensePlate')"
									/>
									<OverviewTableHeader
										:current-sort="currentSortField === 'vehicleIdentificationNumber'"
										:current-sort-dir="currentSortDirection"
										:label="$t('vin').toString()"
										@click.native="sortby('vehicleIdentificationNumber')"
									/>
									<OverviewTableHeader
										:current-sort="currentSortField === 'customer'"
										:current-sort-dir="currentSortDirection"
										:label="
											this.$store.getters.getAuthenticatedUserGroupLevel === '1' || this.$store.getters.getAuthenticatedUserGroupLevel === '2'
												? $t('customer')
												: $t('owner_name')
										"
										@click.native="sortby('customer')"
									/>
									<OverviewTableHeader
										:current-sort="currentSortField === 'customerAddress'"
										:current-sort-dir="currentSortDirection"
										:label="$t('address').toString()"
										@click.native="sortby('customerAddress')"
									/>
									<th class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-center"></th>
								</tr>
							</thead>
							<tbody class="bg-white">
								<tr
									v-for="customer in this.customers"
									:key="customer.id"
									class="tr odd:bg-gray-200 cursor-pointer"
									@click="redirectToTarget(customer.id)"
								>
									<td class="px-6 py-4 border-b border-gray-200">
										{{ licensePlate(customer) }}
									</td>
									<td class="px-6 py-4 border-b border-gray-200">
										{{ vehicleIdentificationNumber(customer) }}
									</td>
									<td class="px-6 py-4 border-b border-gray-200">
										<div>
											{{ customer.companyName }}
										</div>
										<div>{{ customer.firstName }} {{ customer.lastName }}</div>
									</td>
									<td class="px-6 py-4 border-b border-gray-200 justify-items-start">
										<div class="align-start">{{ customer.street }} {{ customer.houseNumber }}</div>
										<div>{{ customer.zipCode }} {{ customer.city }}</div>
									</td>
									<td class="px-6 py-4 border-b border-gray-200 pr-8">
										<div class="bg-sp btn block btn-overview-edit flex items-start justify-start text-white">
											<font-awesome-icon :icon="['fas', 'edit']"></font-awesome-icon>
										</div>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
			<div class="flex justify-between align-middle mb-4 ml-8 mt-4" v-show="customers && customers.length">
				<PaginationNumberOfPages :current-page="current_page" :last-page="last_page" :total="total" class="mr-8 self-center" />
				<div class="flex">
					<PaginationSizeSelect
						style="min-width: 140px; max-width: 140px"
						@input="directSearch"
						v-model="per_page"
						v-show="customers && customers.length"
					/>
					<BasePagination v-model="current_page" :last-page="last_page" :total="total" :is-blocked="blockSearch" class="mr-8" />
				</div>
			</div>
		</div>
		<div v-else-if="isUserAllowedInLevel5or6" class="main">
			<div v-if="isUserAllowedInLevel5or6">
				<BaseCard class="w-full bg-gray-200" :title="$t('own_last_process').toString()" :is-toggleable="false">
					<DashboardProcessList class="w-full flex" :processes="processesLastOwn" v-if="processesLastOwn" />

					<template #icons>
						<BaseCheckbox
							:filter-only="true"
							:label="$t('with_closed')"
							class="mb-0 mt-2"
							v-model="inclusiveClosedProcessesLastOwn"
							@input="getDashboard"
						/>
					</template>
				</BaseCard>

				<BaseCard class="mt-8 w-full bg-gray-200" :title="$t('last_process').toString()" :is-toggleable="false">
					<DashboardProcessList :processes="processesLast" v-if="processesLast" />
					<template #icons>
						<BaseCheckbox
							:filter-only="true"
							:label="$t('with_closed')"
							class="mb-0 mt-2"
							v-model="inclusiveClosedProcessesLast"
							@input="getDashboard"
						/>
					</template>
				</BaseCard>
			</div>
		</div>
	</fragment>
	<Forbidden v-else />
</template>

<script>
import { Fragment } from 'vue-fragment';
import { CustomersApiFactory } from '@/api/generated';
import { getStringFromDate } from '@/plugins/Utils';
import apiDefaults from '@/api/defaultConfiguration';
import BaseHeader from '@/components/navigation/BaseHeader';
import OverviewSearch from '@/components/damagecases/forms/OverviewSearch';
import OverviewTableHeader from '@/views/damagecases/OverviewTableHeader';
import BasePagination from '@/components/forms/buttons/BasePagination';
import PaginationSizeSelect from '@/components/damagecases/forms/PaginationSizeSelect';
import PaginationNumberOfPages from '@/components/forms/buttons/PaginationNumberOfPages';
import Authentication from '@/mixins/Authentication';
import BaseButton from '@/components/forms/buttons/BaseButton';
import AppContainerMixin from '@/mixins/AppContainerMixin';
import Forbidden from '@/views/default/Forbidden.vue';
import DashboardProcessList from '@/views/Dashboard/ProcessList.vue';
import BaseCard from '@/components/damagecases/forms/BaseCard.vue';
import BaseCheckbox from '@/components/forms/inputs/BaseCheckbox.vue';
import DashboardRepository from '@/api/repositories/DashboardRepository';

export default {
	name: 'Management',
	mixins: [AppContainerMixin, Authentication],
	components: {
		BaseCheckbox,
		BaseCard,
		DashboardProcessList,
		Forbidden,
		BaseButton,
		PaginationNumberOfPages,
		PaginationSizeSelect,
		BasePagination,
		OverviewTableHeader,
		OverviewSearch,
		BaseHeader,
		Fragment,
	},
	computed: {
		showAddDamageCaseButton() {
			const isLevel5or6AndMobile = this.isUserAllowedInLevel5or6 && window.appcontainer.isiOSAppcontainer;
			const isLevel3 = this.getAuthenticatedUserGroupLevel === '3';
			return isLevel5or6AndMobile || (!this.isUserAllowedInLevel5or6 && !isLevel3 && !this.isUserAllowedInLevel4 && !this.isUserAllowedInLevel1);
		},
		showStatus() {
			return this.$refs['overviewSearch'].status.value === 'all';
		},
	},
	data() {
		return {
			per_page: { display: '25', value: 25 },
			damageCases: [],
			customers: null,
			searchQuery: undefined,
			timeoutIndex: 0,
			isSearchRunning: false,
			currentSortField: 'customer',
			currentSortDirection: 'asc',
			current_page: 1,
			last_page: 1,
			total: 0,
			blockSearch: false,
			blockPageReset: false,
			inclusiveClosedProcessesLast: false,
			inclusiveClosedProcessesLastOwn: false,
			processesLast: null,
			processesLastOwn: null,
		};
	},
	mounted() {
		this.$emit('scrollUp', this.pageIndex);
		this.getDashboard();
	},
	watch: {
		current_page: function(oldValue, newValue) {
			if (oldValue !== newValue) {
				this.blockPageReset = true;
				this.$refs['overviewSearch'].directSearch();
			}
		},
		per_page: function() {
			this.$refs['overviewSearch'].directSearch();
		},
	},
	methods: {
		licensePlate(customer) {
			if (customer.vehicle?.length) {
				if (customer.vehicle?.length > 1) {
					return 'Fuhrpark';
				}
				return customer.vehicle[0].licensePlate.text;
			}
			return customer.vehicle?.licensePlate?.text;
		},
		vehicleIdentificationNumber(customer) {
			if (customer.vehicle?.length) {
				if (customer.vehicle?.length > 1) {
					return '';
				}
				return customer.vehicle[0].vehicleIdentificationNumber;
			}
			return customer.vehicle?.vehicleIdentificationNumber;
		},
		redirectToTarget(customerId) {
			this.$router.push({ path: '/management/customer/overview/' + customerId });
		},
		search($event) {
			clearTimeout(this.timeoutIndex);
			this.timeoutIndex = setTimeout(() => {
				this.directSearch($event);
			}, 500);
		},
		directSearch($event) {
			if (!this.blockPageReset) {
				this.current_page = 1;
			}

			this.blockSearch = true;
			this.isSearchRunning = true;
			const sort = (this.currentSortDirection === 'desc' ? '-' : '') + this.currentSortField;

			CustomersApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.managementCustomersGet(
					this.current_page,
					this.per_page ? this.per_page.value : undefined,
					sort,
					$event.customer ? $event.customer : undefined,
				)
				.then(response => {
					this.customers = response.data.data;
					this.isSearchRunning = false;
					this.blockSearch = false;
					this.blockPageReset = false;
					this.current_page = parseInt(response.data.meta.current_page);
					this.last_page = response.data.meta.last_page;
					this.total = response.data.meta.total;
				})
				.catch(error => {
					this.isSearchRunning = false;
				});
		},
		getDashboard() {
			if (this.isUserAllowedInLevel5or6) {
				const options = {
					inclusiveClosedProcessesLast: this.inclusiveClosedProcessesLast,
					inclusiveClosedProcessesLastOwn: this.inclusiveClosedProcessesLastOwn,
				};
				new DashboardRepository().getDashboard(options).then(response => {
					this.processesLast = response.data.processesLast;
					this.processesLastOwn = response.data.processesLastOwn;
				});
			}
		},
		showCol: function(showLvl) {
			return showLvl.indexOf(parseInt(this.$store.getters.getAuthenticatedUserGroupLevel)) !== -1;
		},
		sortby: function(field) {
			if (field === this.currentSortField) {
				this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
			} else {
				this.currentSortDirection = 'asc';
			}
			this.currentSortField = field;
			this.$refs['overviewSearch'].directSearch();
		},
		formatDate: function(timestamp) {
			const date = new Date(timestamp * 1000);
			return getStringFromDate(date);
		},
	},
};
</script>
